import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheck } from "react-icons/fi";

const TYPES = ["SAT", "SAT 8/9", "NMSQT", "ACT", "AP Calculus AB", "AP Calculus BC"];
const LEVELS = [
  { value: "1", label: "Beginner" },
  { value: "2", label: "Intermediate" },
  { value: "3", label: "Advanced" },
];

// process.env.PUBLIC_URL handles both dev (localhost) and prod (/dashboard) correctly
const BG_IMAGES = Array.from({ length: 8 }, (_, i) => ({
  id: String(i + 1),
  url: `${process.env.PUBLIC_URL}/bgimg${i + 1}.png`,
}));

const PillBtn = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 border whitespace-nowrap ${
      selected
        ? "bg-white/90 text-zinc-900 border-white/80"
        : "bg-white/[0.04] text-white/45 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/75"
    }`}
  >
    {label}
  </button>
);

function CreateClassModal({ isModalOpen, setIsModalOpen, handleCreateClass }) {
  const [className, setClassName] = useState("");
  const [type, setType]   = useState("");
  const [level, setLevel] = useState("");
  const [bg, setBg]       = useState("");

  const reset = () => { setClassName(""); setType(""); setLevel(""); setBg(""); };

  const canSubmit = className.trim() && type && level && bg;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!className.trim()) { alert("Class name is required."); return; }
    if (!type)             { alert("Please select a type."); return; }
    if (!level)            { alert("Please select a level."); return; }
    if (!bg)               { alert("Please select a background image."); return; }

    handleCreateClass({ className, type, level, bg }, () => {
      reset();
      setIsModalOpen(false);
    });
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(10px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false); }}
        >
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[440px] rounded-2xl overflow-hidden"
            style={{
              background: "#12151f",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 32px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)",
            }}
          >
            {/* Top shimmer line */}
            <div
              className="absolute top-0 left-0 right-0"
              style={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)" }}
            />

            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div>
                <h2 className="text-base font-bold text-white tracking-tight">Create a Class</h2>
                <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.32)" }}>
                  Set up your class details below
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="mt-0.5 w-6 h-6 rounded-md flex items-center justify-center transition-all"
                style={{ color: "rgba(255,255,255,0.28)" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.28)"; }}
              >
                <FaTimes size={11} />
              </button>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", margin: "0 24px", background: "rgba(255,255,255,0.06)" }} />

            {/* Body */}
            <form onSubmit={onSubmit} className="px-6 pt-5 pb-6 space-y-5">

              {/* Class Name */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest mb-2"
                  style={{ color: "rgba(255,255,255,0.32)" }}>
                  Class Name
                </label>
                <input
                  type="text"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  placeholder="e.g. SAT Morning Session"
                  className="w-full rounded-xl px-4 py-2.5 text-sm text-white outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    caretColor: "white",
                  }}
                  onFocus={e => (e.target.style.border = "1px solid rgba(255,255,255,0.24)")}
                  onBlur={e  => (e.target.style.border = "1px solid rgba(255,255,255,0.09)")}
                />
              </div>

              {/* Exam Type — pill group */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest mb-2.5"
                  style={{ color: "rgba(255,255,255,0.32)" }}>
                  Exam Type
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {TYPES.map((t) => (
                    <PillBtn key={t} label={t} selected={type === t} onClick={() => setType(t)} />
                  ))}
                </div>
              </div>

              {/* Level — pill group */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest mb-2.5"
                  style={{ color: "rgba(255,255,255,0.32)" }}>
                  Level
                </label>
                <div className="flex gap-1.5">
                  {LEVELS.map((l) => (
                    <PillBtn key={l.value} label={l.label} selected={level === l.value} onClick={() => setLevel(l.value)} />
                  ))}
                </div>
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest mb-2.5"
                  style={{ color: "rgba(255,255,255,0.32)" }}>
                  Cover Image
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {BG_IMAGES.map((item) => {
                    const isSelected = bg === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setBg(item.id)}
                        className="relative rounded-xl overflow-hidden focus:outline-none"
                        style={{
                          height: "60px",
                          border: isSelected
                            ? "2px solid rgba(255,255,255,0.70)"
                            : "2px solid rgba(255,255,255,0.07)",
                          transform: isSelected ? "scale(1.06)" : "scale(1)",
                          opacity: bg && !isSelected ? 0.45 : 1,
                          transition: "all 0.15s ease",
                        }}
                      >
                        <img
                          src={item.url}
                          alt={`Cover ${item.id}`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                        {isSelected && (
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{ background: "rgba(0,0,0,0.35)" }}
                          >
                            <span
                              className="flex items-center justify-center rounded-full"
                              style={{
                                width: 20, height: 20,
                                background: "white",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                              }}
                            >
                              <FiCheck size={11} strokeWidth={3} color="#12151f" />
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: canSubmit
                    ? "linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)"
                    : "rgba(255,255,255,0.05)",
                  color: canSubmit ? "white" : "rgba(255,255,255,0.2)",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  boxShadow: canSubmit ? "0 4px 18px rgba(29,78,216,0.35)" : "none",
                }}
              >
                Create Class
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CreateClassModal;
